import React from 'react';
import {
  Box,
  styled,
  Typography,
  Breadcrumbs as MuiBreadcrumbs,
  Link,
  Grid,
  IconButton,
  Tooltip,
} from '@mui/material';
import { useLayoutContext } from 'contexts/LayoutContext';
import { useAiAgent } from 'contexts/AiAgentContext';
import useAuth from 'hooks/auth/useAuth';
import { AdminUserRealm } from 'further-types/admin-user';
import { Menu } from '@mui/icons-material';
import { useHistory } from 'react-router-dom';
import { useFlag } from '@reflag/react-sdk';
import GradientAiIcon from 'components/icons/GradientAiIcon';
import GlobalSearch from './GlobalSearch';
import { Row } from 'components/Layout';

const Wrapper = styled(Box)(({ theme }) => ({
  height: theme.searchBar.height,
  padding: theme.spacing(theme.spacingSizes.xs, theme.spacingSizes.sm),
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexDirection: 'row',
}));

const GridContainer = styled(Grid)({
  width: '100%',
  alignItems: 'center',
  justifyContent: 'center',
});

const InfoContainer = styled(Grid)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
});

const LogoWrapper = styled(Box)(({ theme }) => ({
  width: '100%',
  textAlign: 'center',
  display: 'none',

  [theme.breakpoints.down(theme.sidebar.drawerBreakpoint)]: {
    display: 'block',
  },
}));

const Logo = styled('img')({
  height: '24px',
});

const Breadcrumbs = styled(MuiBreadcrumbs)(({ theme }) => ({
  [theme.breakpoints.down(theme.sidebar.drawerBreakpoint)]: {
    display: 'none',
  },
}));

const BreadcrumbLink = styled(Link)(({ theme }) => ({
  cursor: 'pointer',
  display: 'flex',
  gap: theme.spacing(theme.spacingSizes.xs),
  alignItems: 'center',
}));

const MenuToggle = styled(IconButton)(({ theme }) => ({
  display: 'none',
  [theme.breakpoints.down(theme.sidebar.drawerBreakpoint)]: {
    display: 'flex',
  },
}));

const SearchBar: React.FC = () => {
  const history = useHistory();
  const {
    breadcrumbs,
    setSidebarOpen,
    isSidebarOpen,
    pageIcon: PageIcon,
  } = useLayoutContext();
  const {
    open: openAiAgent,
    close: closeAiAgent,
    isOpen: isAiAgentOpen,
  } = useAiAgent();
  const { realm } = useAuth();
  const { isEnabled: showAiAgent } = useFlag('show-experimental-ai-agent');
  const isLpRealm = realm === AdminUserRealm.Lp;

  return (
    <Wrapper>
      <GridContainer container spacing={2} direction="row">
        <Grid size={4}>
          <MenuToggle
            onClick={() => {
              setSidebarOpen(!isSidebarOpen);
            }}
            edge="start"
            color="inherit"
            aria-label="menu"
            size="large"
          >
            <Menu />
          </MenuToggle>
          <Breadcrumbs aria-label="breadcrumb" maxItems={3}>
            {breadcrumbs.map((breadcrumb, index) => {
              const label =
                typeof breadcrumb === 'string' ? breadcrumb : breadcrumb.label;
              const href =
                typeof breadcrumb === 'string' ? null : breadcrumb.link;

              return href ? (
                <BreadcrumbLink
                  key={label}
                  underline="hover"
                  color="grey.900"
                  onClick={() => {
                    history.push(href);
                  }}
                  variant="h6"
                >
                  {index === 0 && PageIcon ? (
                    <PageIcon fontSize="small" />
                  ) : null}
                  {label}
                </BreadcrumbLink>
              ) : (
                <Row centered spacing="xs" key={label}>
                  {index === 0 && PageIcon ? (
                    <PageIcon fontSize="small" />
                  ) : null}
                  <Typography variant="h6">{label}</Typography>
                </Row>
              );
            })}
          </Breadcrumbs>
        </Grid>
        <Grid size={4}>
          {isLpRealm ? <GlobalSearch /> : null}
          <LogoWrapper>
            <Logo src="images/logo.png" alt="Further logo" />
          </LogoWrapper>
        </Grid>
        <InfoContainer size={4}>
          {showAiAgent && isLpRealm ? (
            <Tooltip title="AI Agent">
              <IconButton
                size="small"
                onClick={isAiAgentOpen ? closeAiAgent : openAiAgent}
              >
                <GradientAiIcon size={18} />
              </IconButton>
            </Tooltip>
          ) : null}
        </InfoContainer>
      </GridContainer>
    </Wrapper>
  );
};

export default SearchBar;
